// File: frontend/src/i18n/voice.js
// Version: 1.0 (2026-10-18, Task T1.1)
// Description: Bilingual EN/RU dictionary for Voice Operations (VoiceDashboard & VoiceAnalytics).
//              Provides 100% key parity, variable substitution, and Russian plural declensions (sklonit).

const EN = {
    // ── Voice Operations Overview (Dashboard Header & Controls) ───────────────
    'voice.dashboard.title': 'Voice Operations Overview',
    'voice.dashboard.subtitle': 'Aggregated metrics across {days} days',
    'voice.dashboard.days_btn': '{days} Days',
    'voice.dashboard.err_load': 'Error loading voice stats: {error}',
    'voice.dashboard.detailed_analytics': 'Detailed Analytics',
    'voice.dashboard.detailed_analytics_sub': 'Deep dive into your voice AI metrics',
    'voice.dashboard.no_calls': 'No calls in window',
    'voice.dashboard.cost_subtext': 'Avg ${avg} / call ({calls})',
    'voice.dashboard.cost_zero_subtext': '$0.00 / call (0 calls)',

    // ── Dashboard Top Metric Cards ───────────────────────────────────────────
    'voice.metric.total_cost': 'Total Cost',
    'voice.metric.hangup_analysis': 'Hangup Analysis',
    'voice.metric.conversion_rate': 'Conversion Rate',
    'voice.metric.avg_handle_time': 'Avg. Handle Time',

    // ── Dashboard Tabs ───────────────────────────────────────────────────────
    'voice.tab.analytics': 'Analytics',
    'voice.tab.history': 'Call History',
    'voice.tab.monitor': 'Live Monitor',
    'voice.tab.config': 'Configuration',

    // ── Voice Performance (VoiceAnalytics Lower Section) ──────────────────────
    'voice.analytics.title': 'Voice Performance',
    'voice.analytics.subtitle': 'For the selected period',
    'voice.analytics.trends_title': 'Analytics Trends',
    'voice.analytics.load_error': 'Failed to load analytics data.',
    'voice.analytics.no_data': 'No data available',

    // ── Duration Formatting ──────────────────────────────────────────────────
    'voice.duration.m_s': '{m}m {s}s',
    'voice.duration.m': '{m}m',
    'voice.duration.s': '{s}s',
    'voice.duration.zero': '0m 0s',

    // ── VoiceAnalytics Lower Metric Cards ────────────────────────────────────
    'voice.analytics.total_calls': 'Total Calls',
    'voice.analytics.sub_total_calls': 'Inbound & Outbound',
    'voice.analytics.avg_duration': 'Avg Duration',
    'voice.analytics.conversion_rate': 'Conversion Rate',
    'voice.analytics.unique_callers': 'Unique Callers',
    'voice.analytics.sub_unique_callers': 'Distinct phone numbers',

    // ── VoiceAnalytics Chart Tabs ────────────────────────────────────────────
    'voice.analytics.tab_volume': 'Volume',
    'voice.analytics.tab_sentiment': 'Sentiment',
    'voice.analytics.tab_outcomes': 'Outcomes',
    'voice.analytics.tab_hourly': 'Hourly',

    // ── Plural Categories for sklonit ────────────────────────────────────────
    // 1. Consultations booked
    'voice.plural.consultations_booked.one': '{count} consultation booked',
    'voice.plural.consultations_booked.few': '{count} consultations booked',
    'voice.plural.consultations_booked.many': '{count} consultations booked',

    // 2. Across N calls
    'voice.plural.calls_declined.one': 'Across {count} call',
    'voice.plural.calls_declined.few': 'Across {count} calls',
    'voice.plural.calls_declined.many': 'Across {count} calls',

    // 3. Most common end reason (N calls)
    'voice.plural.calls_reason.one': 'Most common end reason ({count} call)',
    'voice.plural.calls_reason.few': 'Most common end reason ({count} calls)',
    'voice.plural.calls_reason.many': 'Most common end reason ({count} calls)',

    // 4. Calls count short (for cost subtext)
    'voice.plural.calls_short.one': '{count} call',
    'voice.plural.calls_short.few': '{count} calls',
    'voice.plural.calls_short.many': '{count} calls',

    // 5. Calls count full
    'voice.plural.calls_full.one': '{count} call',
    'voice.plural.calls_full.few': '{count} calls',
    'voice.plural.calls_full.many': '{count} calls',

    // ── Voice Configuration Panel ─────────────────────────────────────────────
    'voice.config.first_message_note': 'Saved only in OS database; does not affect calls — managed in Vapi dashboard.',
    'voice.config.voice_provider_note': 'Voice is saved only in OS database; does not affect calls — managed in Vapi dashboard.',
    'voice.config.vapi_status_title': 'Vapi Production Status',
    'voice.config.vapi_assistant_active': 'Active Vapi Assistant: {id}',
    'voice.config.vapi_not_linked': 'not linked',
    'voice.config.vapi_explanation': 'Calls are handled by Vapi assistant {id}. The prompt is compiled from code (sofia_persona) and deployed via sync; this form does not affect live calls.',
    'voice.config.vapi_live_prompt_info': 'Active Production Prompt (Read-only): {chars} chars | SHA-256: {hash}',
    'voice.config.vapi_prompt_origin_note': 'Compiled from code in this version; match with Vapi verified during deployment',
    'voice.config.vapi_form_length': 'Form draft text length: {chars} chars',
    'voice.config.vapi_last_sync': 'Last sync: {date}',
    'voice.config.vapi_never': 'never',
    'voice.config.vapi_pull_btn': 'Refresh from Vapi',
    'voice.config.vapi_pulling': 'Loading…',
    'voice.config.prompt_warning_unlinked': '⚠️ Live calls are handled by Vapi assistant {id} (sofia_persona). Edits here do not affect live calls.',
    'voice.config.provider_label': 'Voice Engine Provider',
    'voice.config.provider_vapi_prod': 'Vapi.ai (Production)',
    'voice.config.pull_success': 'Loaded from Vapi: {chars} chars (lang {lang}). This is the text currently active on calls.',
    'voice.config.pull_error': 'Failed to load from Vapi: {error}',
    'voice.config.sync_pushed': 'System prompt sent to Vapi: {chars} chars (lang {lang}). {rest}⚠️ If you have the Vapi dashboard open, refresh it (⌘R). Otherwise "Publish" from there will overwrite this text.',
    'voice.config.sync_not_pushed': 'Other fields ({fields}) saved only in OS database and do not affect calls — change them in Vapi dashboard. ',
    'voice.config.sync_skipped': 'Saved ONLY in OS database. Not pushed live: this language is not linked to a Vapi assistant, so edits do not affect calls.',
    'voice.config.sync_routing_saved': 'Routing settings saved. Vapi sync was not performed.',
    'voice.config.sync_error_verify_failed': '🔴 Write to Vapi SUCCEEDED, but post-write verification failed: {reason} Nothing saved in OS database.',
    'voice.config.sync_error_not_pushed': 'NOT pushed to Vapi: {reason} Nothing saved — not in Vapi, not in OS database, not routing settings. Text remains in the form.',

    // ── Call History / Recordings ─────────────────────────────────────────────
    'voice.history.call_not_in_list': 'Call not in the current list — widen the period',
};

const RU = {
    // ── Voice Operations Overview (Dashboard Header & Controls) ───────────────
    'voice.dashboard.title': 'Сводка голосового помощника',
    'voice.dashboard.subtitle': 'Метрики за выбранные {days} дн.',
    'voice.dashboard.days_btn': '{days} дн.',
    'voice.dashboard.err_load': 'Ошибка загрузки статистики звонков: {error}',
    'voice.dashboard.detailed_analytics': 'Подробная аналитика',
    'voice.dashboard.detailed_analytics_sub': 'Детальный анализ показателей голосового AI',
    'voice.dashboard.no_calls': 'Нет звонков в периоде',
    'voice.dashboard.cost_subtext': 'В среднем ${avg} за звонок ({calls})',
    'voice.dashboard.cost_zero_subtext': '$0.00 за звонок (0 зв.)',

    // ── Dashboard Top Metric Cards ───────────────────────────────────────────
    'voice.metric.total_cost': 'Общие расходы',
    'voice.metric.hangup_analysis': 'Причина завершения',
    'voice.metric.conversion_rate': 'Конверсия',
    'voice.metric.avg_handle_time': 'Средняя длительность',

    // ── Dashboard Tabs ───────────────────────────────────────────────────────
    'voice.tab.analytics': 'Аналитика',
    'voice.tab.history': 'История звонков',
    'voice.tab.monitor': 'Прямой эфир',
    'voice.tab.config': 'Настройки',

    // ── Voice Performance (VoiceAnalytics Lower Section) ──────────────────────
    'voice.analytics.title': 'Показатели звонков',
    'voice.analytics.subtitle': 'За выбранный период',
    'voice.analytics.trends_title': 'Тренды аналитики',
    'voice.analytics.load_error': 'Не удалось загрузить аналитику.',
    'voice.analytics.no_data': 'Нет данных',

    // ── Duration Formatting ──────────────────────────────────────────────────
    'voice.duration.m_s': '{m} мин {s} с',
    'voice.duration.m': '{m} мин',
    'voice.duration.s': '{s} с',
    'voice.duration.zero': '0 мин 0 с',

    // ── VoiceAnalytics Lower Metric Cards ────────────────────────────────────
    'voice.analytics.total_calls': 'Всего звонков',
    'voice.analytics.sub_total_calls': 'Входящие и исходящие',
    'voice.analytics.avg_duration': 'Средняя длительность',
    'voice.analytics.conversion_rate': 'Конверсия',
    'voice.analytics.unique_callers': 'Уникальные абоненты',
    'voice.analytics.sub_unique_callers': 'Разных номеров',

    // ── VoiceAnalytics Chart Tabs ────────────────────────────────────────────
    'voice.analytics.tab_volume': 'Объём',
    'voice.analytics.tab_sentiment': 'Тональность',
    'voice.analytics.tab_outcomes': 'Итоги',
    'voice.analytics.tab_hourly': 'По часам',

    // ── Plural Categories for sklonit ────────────────────────────────────────
    // 1. Consultations booked: 1 запись, 2..4 записи, 5..20 записей
    'voice.plural.consultations_booked.one': '{count} запись на консультацию',
    'voice.plural.consultations_booked.few': '{count} записи на консультацию',
    'voice.plural.consultations_booked.many': '{count} записей на консультацию',

    // 2. Across N calls: По 1 звонку, По 3 звонкам, По 5 звонкам
    'voice.plural.calls_declined.one': 'По {count} звонку',
    'voice.plural.calls_declined.few': 'По {count} звонкам',
    'voice.plural.calls_declined.many': 'По {count} звонкам',

    // 3. Most common end reason: 1 звонок, 3 звонка, 5 звонков
    'voice.plural.calls_reason.one': 'Основная причина ({count} звонок)',
    'voice.plural.calls_reason.few': 'Основная причина ({count} звонка)',
    'voice.plural.calls_reason.many': 'Основная причина ({count} звонков)',

    // 4. Calls count short (for cost subtext)
    'voice.plural.calls_short.one': '{count} зв.',
    'voice.plural.calls_short.few': '{count} зв.',
    'voice.plural.calls_short.many': '{count} зв.',

    // 5. Calls count full: 1 звонок, 3 звонка, 5 звонков
    'voice.plural.calls_full.one': '{count} звонок',
    'voice.plural.calls_full.few': '{count} звонка',
    'voice.plural.calls_full.many': '{count} звонков',

    // ── Voice Configuration Panel ─────────────────────────────────────────────
    'voice.config.first_message_note': 'Сохраняется только в базе OS; на звонки не влияет — меняется в панели Vapi.',
    'voice.config.voice_provider_note': 'Голос сохраняется только в базе OS; на звонки не влияет — меняется в панели Vapi.',
    'voice.config.vapi_status_title': 'Статус Vapi в бою',
    'voice.config.vapi_assistant_active': 'Активный ассистент Vapi: {id}',
    'voice.config.vapi_not_linked': 'не привязан',
    'voice.config.vapi_explanation': 'Звонки ведёт ассистент Vapi {id}. Промпт собирается из кода (sofia_persona) и выкатывается синхронизацией; эта форма на звонки не влияет.',
    'voice.config.vapi_live_prompt_info': 'Боевой промпт (только чтение): {chars} знаков | SHA-256: {hash}',
    'voice.config.vapi_prompt_origin_note': 'собран из кода этой версии; совпадение с Vapi проверяется сверкой при выкатке',
    'voice.config.vapi_form_length': 'Длина текста в форме: {chars} знаков',
    'voice.config.vapi_last_sync': 'Последняя синхронизация: {date}',
    'voice.config.vapi_never': 'никогда',
    'voice.config.vapi_pull_btn': 'Обновить из Vapi',
    'voice.config.vapi_pulling': 'Загружаю…',
    'voice.config.prompt_warning_unlinked': '⚠️ Звонки ведёт ассистент Vapi {id} (sofia_persona). Правка в этой форме не влияет на звонки.',
    'voice.config.provider_label': 'Провайдер голосового движка',
    'voice.config.provider_vapi_prod': 'Vapi.ai (В бою)',
    'voice.config.pull_success': 'Загружено из Vapi: {chars} знаков (язык {lang}). Это текст, который исполняется на звонке.',
    'voice.config.pull_error': 'Не удалось загрузить из Vapi: {error}',
    'voice.config.sync_pushed': 'В Vapi уехал СИСТЕМНЫЙ ПРОМПТ: {chars} знаков (язык {lang}). {rest}⚠️ Если у вас открыта вкладка панели Vapi — обновите её (⌘R). Иначе «Publish» из неё вернёт старый текст.',
    'voice.config.sync_not_pushed': 'Остальное ({fields}) сохранено только в базе OS и на звонки НЕ влияет — эти поля меняются в панели Vapi. ',
    'voice.config.sync_skipped': 'Сохранено ТОЛЬКО в базе OS. В эфир НЕ уехало: этот язык не привязан к ассистенту Vapi, поэтому правка на звонки не влияет.',
    'voice.config.sync_routing_saved': 'Настройки маршрутизации сохранены. Синхронизация с Vapi не выполнялась.',
    'voice.config.sync_error_verify_failed': '🔴 Запись в Vapi ВЫПОЛНЕНА, но сверка после записи не сошлась: {reason} В базе OS не сохранено ничего.',
    'voice.config.sync_error_not_pushed': 'НЕ уехало в Vapi: {reason} Не сохранено НИЧЕГО — ни в Vapi, ни в базе OS, ни настройки маршрутизации. Текст остался в форме.',

    // ── Call History / Recordings ─────────────────────────────────────────────
    'voice.history.call_not_in_list': 'Звонка нет в текущем списке — расширьте период',
};

export const SLOVAR = Object.freeze({
    en: Object.freeze(EN),
    ru: Object.freeze(RU),
});

export function pluralCategory(lang, n) {
    const num = Math.abs(Number(n) || 0);
    if (lang === 'ru') {
        const rem100 = num % 100;
        const rem10 = num % 10;
        if (rem100 >= 11 && rem100 <= 14) return 'many';
        if (rem10 === 1) return 'one';
        if (rem10 >= 2 && rem10 <= 4) return 'few';
        return 'many';
    }
    return num === 1 ? 'one' : 'many';
}

export function sklonit(lang, baseKey, count, vars = {}) {
    const cat = pluralCategory(lang, count);
    const key = `${baseKey}.${cat}`;
    return perevesti(lang, key, { ...vars, count, n: count });
}

export function perevesti(lang, key, vars) {
    const yazyk = lang === 'ru' ? 'ru' : 'en';
    const slovar = SLOVAR[yazyk];
    if (!slovar || !Object.prototype.hasOwnProperty.call(slovar, key)) {
        return `[${key}]`;
    }
    const shablon = slovar[key];
    const peremennye = vars && typeof vars === 'object' ? vars : {};
    return shablon.replace(/\{([a-zA-Z_]+)\}/g, (celoe, imya) => (
        Object.prototype.hasOwnProperty.call(peremennye, imya) &&
        peremennye[imya] !== undefined &&
        peremennye[imya] !== null
            ? String(peremennye[imya])
            : celoe
    ));
}

export function raznitsaKlyuchey(a, b) {
    const vB = b || {};
    return Object.keys(a || {})
        .filter((k) => !Object.prototype.hasOwnProperty.call(vB, k))
        .sort();
}

export function formatVoiceDuration(rawDuration, t) {
    if (!rawDuration || typeof rawDuration !== 'string') {
        return rawDuration || (t ? t('voice.duration.zero') : '0m 0s');
    }
    const trimmed = rawDuration.trim();
    const match = trimmed.match(/^(?:(\d+)\s*m)?\s*(?:(\d+)\s*s)?$/i);
    if (!match || (!match[1] && !match[2])) {
        return rawDuration;
    }
    const m = match[1];
    const s = match[2];
    if (m && s) {
        return t ? t('voice.duration.m_s', { m, s }) : `${m}m ${s}s`;
    }
    if (m) {
        return t ? t('voice.duration.m', { m }) : `${m}m`;
    }
    if (s) {
        return t ? t('voice.duration.s', { s }) : `${s}s`;
    }
    return t ? t('voice.duration.zero') : '0m 0s';
}
